// Each write clears the node cache too, so the Workflow Builder node picker
// can't serve a stale catalog.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNodeStore } from '@/stores/node-store';
import {
  createOrgMcpTool,
  deleteOrgMcpTool,
  refreshOrgMcpToolTools,
  toggleOrgMcpTool,
  updateOrgMcpTool,
  validateOrgMcpTool,
} from '@/services/org-mcp-tools/org-mcp-tools';
import {
  OrgMcpToolCreateInput,
  OrgMcpToolUpdateInput,
} from '@/services/org-mcp-tools/types';
import { orgMcpToolsQueryKey } from '@/hooks/queries/org-mcp-tools/use-list-org-mcp-tools';

const useAfterWrite = (orgId?: string) => {
  const queryClient = useQueryClient();
  // Awaits the list refetch so the caller's mutateAsync (and thus
  // mutation.isPending) stays pending until the cache reflects the write. The
  // per-tool toggle handler recomputes selected_tools from the cached tool
  // object, so resolving before the refetch would let a rapid follow-up toggle
  // read the stale allowlist and clobber the previous change.
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: orgMcpToolsQueryKey(orgId),
    });
    // Node picker reads the catalog; drop its cache so the org tool shows /
    // disappears immediately rather than after the ~15min factory TTL.
    useNodeStore.getState().clearNodeCache();
  };
};

// Validate does not mutate server state, so it skips cache invalidation.
export const useValidateOrgMcpTool = (orgId?: string) =>
  useMutation({
    mutationFn: (input: OrgMcpToolCreateInput) =>
      validateOrgMcpTool(orgId!, input),
  });

export const useCreateOrgMcpTool = (orgId?: string) => {
  const afterWrite = useAfterWrite(orgId);
  return useMutation({
    mutationFn: (input: OrgMcpToolCreateInput) =>
      createOrgMcpTool(orgId!, input),
    onSuccess: afterWrite,
  });
};

export const useUpdateOrgMcpTool = (orgId?: string) => {
  const afterWrite = useAfterWrite(orgId);
  return useMutation({
    mutationFn: ({
      toolId,
      input,
    }: {
      toolId: string;
      input: OrgMcpToolUpdateInput;
    }) => updateOrgMcpTool(orgId!, toolId, input),
    onSuccess: afterWrite,
  });
};

export const useToggleOrgMcpTool = (orgId?: string) => {
  const afterWrite = useAfterWrite(orgId);
  return useMutation({
    mutationFn: ({ toolId, enabled }: { toolId: string; enabled: boolean }) =>
      toggleOrgMcpTool(orgId!, toolId, enabled),
    onSuccess: afterWrite,
  });
};

export const useRefreshOrgMcpTool = (orgId?: string) => {
  const afterWrite = useAfterWrite(orgId);
  return useMutation({
    mutationFn: (toolId: string) => refreshOrgMcpToolTools(orgId!, toolId),
    onSuccess: afterWrite,
  });
};

export const useDeleteOrgMcpTool = (orgId?: string) => {
  const afterWrite = useAfterWrite(orgId);
  return useMutation({
    mutationFn: (toolId: string) => deleteOrgMcpTool(orgId!, toolId),
    onSuccess: afterWrite,
  });
};
